/**
 * @fileoverview "Secured party contacts" section for the SI detail panel.
 *
 * Shows every entity on the registration (lead + joint parties, MOR-708)
 * resolved to its CRM contact — so a joint "Solaris Group" registration
 * reads as its eight member entities, not just the lead — and lets the
 * practitioner repoint the LEAD secured party at a different job contact
 * when the agent attributed the registration to the wrong entity.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ChevronRight, Star, UserCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { ContactDetailPanel } from "@/components/jobs/phases/contacts/ContactDetailPanel";

interface SecuredPartyContactsSectionProps {
  jobId: Id<"jobs">;
  entryId: Id<"securityInterestEntries">;
  isViewer?: boolean;
}

export function SecuredPartyContactsSection({
  jobId,
  entryId,
  isViewer,
}: SecuredPartyContactsSectionProps) {
  const { data: parties } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.securityInterests.publicQueries
        .listSecurityInterestParties,
      { entryId },
    ),
  );
  const { data: contacts } = useQuery(
    convexQuery(api.contacts.queries.listJobContacts, { jobId }),
  );
  const setSecuredParty = useConvexMutation(
    api.jobs.summarization.agents.securityInterests.publicMutations
      .setSecurityInterestSecuredPartyFromContact,
  );

  const [changing, setChanging] = useState(false);
  const [pickedContactId, setPickedContactId] = useState<string>("");
  const [viewingContactId, setViewingContactId] =
    useState<Id<"jobContacts"> | null>(null);

  if (!parties || parties.length === 0) return null;

  const contactOptions = (contacts ?? [])
    .filter((c) => c.status === "active")
    .map((c) => ({
      value: c._id,
      label: c.displayName,
      title: c.acn ? `ACN ${c.acn}` : c.abn ? `ABN ${c.abn}` : undefined,
    }));

  const handleChange = async () => {
    try {
      await setSecuredParty({
        entryId,
        contactId: pickedContactId as Id<"jobContacts">,
      });
      toast.success("Secured party updated");
      setChanging(false);
      setPickedContactId("");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not update secured party",
      );
    }
  };

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <Users className="h-3.5 w-3.5" />
          Secured party contacts
          <Badge color="gray" variant="soft" size="sm">
            {parties.length}
          </Badge>
        </h3>
        {!isViewer && !changing && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setChanging(true)}
          >
            Change secured party
          </Button>
        )}
      </div>

      <ul className="mt-2 flex flex-col gap-1.5">
        {parties.map((party, i) => {
          const inner = (
            <>
              <Star
                className={
                  party.isLead
                    ? "h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400"
                    : "h-3.5 w-3.5 shrink-0 text-muted-foreground/25"
                }
                aria-label={party.isLead ? "Lead secured party" : undefined}
              />
              <div className="min-w-0 flex-1 text-left">
                <div className="truncate text-sm font-medium text-foreground">
                  {party.name}
                </div>
                {party.identifier && (
                  <div className="font-mono text-[11px] tabular-nums text-muted-foreground">
                    {party.identifierType} {party.identifier}
                  </div>
                )}
              </div>
              {party.groupName && (
                <Badge
                  color="blue"
                  variant="soft"
                  size="sm"
                  className="shrink-0"
                >
                  {party.groupName}
                </Badge>
              )}
              {party.contactId ? (
                <span
                  title={`Open contact ${party.contactDisplayName ?? party.name}`}
                  className="flex shrink-0 items-center gap-0.5 text-emerald-600"
                >
                  <UserCheck className="h-3.5 w-3.5" />
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40" />
                </span>
              ) : (
                <span className="shrink-0 text-[11px] text-muted-foreground/60">
                  No contact
                </span>
              )}
            </>
          );
          // Linked parties open the contact slide-out (mirrors the
          // linked-records rows); unlinked parties render inert.
          const linkedContactId = party.contactId;
          return linkedContactId ? (
            <li key={i}>
              <button
                type="button"
                onClick={() => setViewingContactId(linkedContactId)}
                className="flex w-full items-center gap-2 rounded-lg border border-border bg-muted/10 px-3 py-2 transition-colors hover:bg-muted/40"
              >
                {inner}
              </button>
            </li>
          ) : (
            <li
              key={i}
              className="flex items-center gap-2 rounded-lg border border-border bg-muted/10 px-3 py-2"
            >
              {inner}
            </li>
          );
        })}
      </ul>

      {/* Contact slide-out for a clicked secured party */}
      <ContactDetailPanel
        jobId={jobId}
        contactId={viewingContactId}
        open={viewingContactId !== null}
        onClose={() => setViewingContactId(null)}
        jobContacts={contacts ?? []}
      />

      {changing && (
        <div className="mt-2 flex flex-col gap-2 rounded-lg border border-border bg-muted/20 p-3">
          <p className="text-xs text-muted-foreground">
            Pick the contact that actually holds this registration — the lead
            secured party's name, identifier, and address update from it, and
            the row is locked against agent overwrites.
          </p>
          <SearchableSelect
            value={pickedContactId}
            onValueChange={setPickedContactId}
            options={contactOptions}
            placeholder="Choose a contact…"
            searchPlaceholder="Search contacts…"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={!pickedContactId}
              onClick={() => void handleChange()}
            >
              Update secured party
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setChanging(false);
                setPickedContactId("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="mt-4 border-b" />
    </div>
  );
}
